/**
 * Klien HTTP untuk sync engine (dipakai P5; layar memakai cache, bukan ini).
 *
 * Hanya `syncEngine` dan `mediaQueue` yang boleh memanggil jaringan — itu yang
 * menjamin tampilan online dan offline identik (§10.2). Token disimpan di memori
 * modul, bukan `localStorage`: XSS yang berhasil tetap tidak menemukannya
 * tersimpan di tempat yang dapat dibaca setelah tab ditutup.
 */

/**
 * Origin API dipisahkan dari halaman: PWA lapangan dilayani sebagai aset statis
 * dan tidak selalu satu asal dengan API, jadi jalur relatif akan menabrak
 * `index.html` alih-alih server. Bentuknya sama dengan back office supaya satu
 * nilai `.env` melayani keduanya.
 */
const BASE_URL = `${(import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3000'}/api/v1`;

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function hasAccessToken(): boolean {
  return accessToken !== null;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Kegagalan yang tidak akan berubah kalau diulang dengan payload yang sama. */
  get isPermanent(): boolean {
    return this.status === 400 || this.status === 403 || this.status === 404 || this.status === 422;
  }
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Kirim `Buffer`/`Blob` apa adanya, tanpa membungkusnya sebagai JSON. */
  raw?: BodyInit;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { ...options.headers };
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  const response = await fetch(`${BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.raw ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
    signal: options.signal,
    credentials: 'include',
  });

  const text = await response.text();
  const parsed = text.length > 0 ? (JSON.parse(text) as { data?: T; error?: ApiErrorBody }) : {};

  if (!response.ok) {
    throw new ApiError(
      response.status,
      parsed.error?.code ?? 'UNKNOWN',
      parsed.error?.message ?? `Permintaan gagal (${response.status})`,
    );
  }

  return parsed.data as T;
}

interface ApiErrorBody {
  code: string;
  message: string;
}
